import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';

import { history } from '../../store';
import { locationsActions } from '../../behavior/locations';

// Scoped MUI theme. No CssBaseline — the rest of the app (Semantic UI) is untouched.
const searchTheme = createTheme({
  typography: { fontFamily: 'inherit', fontSize: 13 },
  palette: {
    mode: 'light',
    primary: { main: '#1a1a1a' },
    divider: 'rgba(0, 0, 0, 0.12)',
    background: { paper: '#ffffff' },
  },
  shape: { borderRadius: 4 },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: { backgroundColor: '#fff', fontFamily: 'inherit' },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: { fontFamily: 'inherit', fontSize: 13, paddingBlock: 4 },
        option: { paddingBlock: 6 },
      },
    },
  },
});

const startsWith = (option, q) =>
  String(option.buildingNum).startsWith(q) ||
  option.locationInfoWithAcronym?.toLowerCase().startsWith(q);

const filterOptions = (options, { inputValue }) => {
  const q = inputValue.trim().toLowerCase();
  if (!q) return options.slice(0, 100);
  const matched = options.filter(o =>
    o.locationInfoWithAcronym?.toLowerCase().includes(q),
  );
  matched.sort((a, b) => (startsWith(a, q) ? 0 : 1) - (startsWith(b, q) ? 0 : 1));
  return matched.slice(0, 100);
};

const BSearch = ({ buildingList, getBuildingList, isLoadingLocationList, compact = false }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const isLoading = isLoadingLocationList && !buildingList?.length;

  useEffect(() => {
    if (!buildingList || buildingList.length === 0) getBuildingList();
  }, []);

  const navigateTo = location => {
    if (location) history.push(`/buildings/num/${location.buildingNum}`);
  };

  const renderOption = (props, option, { inputValue }) => {
    const label = option.locationInfoWithAcronym ?? '';
    const q = inputValue.trim();
    if (!q) return <li {...props} key={option.id}>{label}</li>;

    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = label.split(new RegExp(`(${escaped})`, 'gi'));
    return (
      <li {...props} key={option.id}>
        {parts.map((part, i) =>
          part.toLowerCase() === q.toLowerCase() ? (
            <strong key={i}>{part}</strong>
          ) : (
            part
          ),
        )}
      </li>
    );
  };

  return (
    <ThemeProvider theme={searchTheme}>
      <Box sx={{ width: '100%', maxWidth: compact ? 360 : '100%', py: compact ? 0 : 0.5 }}>
        <Autocomplete
          size={compact ? 'small' : 'medium'}
          autoHighlight
          options={buildingList || []}
          loading={isLoading}
          aria-busy={isLoading}
          value={selected}
          onChange={(_, value) => {
            setSelected(value);
            navigateTo(value);
          }}
          getOptionLabel={option => option?.locationInfoWithAcronym ?? ''}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={filterOptions}
          renderOption={renderOption}
          renderInput={params => (
            <TextField
              {...params}
              autoFocus={!compact}
              placeholder={t('building.generic.search.for.a.building.placeholder')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading && (
                      <CircularProgress
                        color="inherit"
                        size={16}
                        aria-label={t('building.generic.search.for.a.building.label')}
                      />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.25, mr: -0.25 }}>
                    <SearchIcon fontSize="small" sx={{ color: 'rgba(0,0,0,0.45)' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Box>
    </ThemeProvider>
  );
};

const mapStateToProps = state => ({
  ...state.locations,
});

const mapDispatchToProps = dispatch => ({
  getBuildingList: () => dispatch(locationsActions.getLocationAutocompleteList(true)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(BSearch);
